import * as CollisionLogicUtil from "./collisionWarning/CollisionLogicUtil";
import * as LightWeightGlobalLogic from "./collisionWarning/LightWeightGlobalLogic";
import Location from "./collisionWarning/Location";
import Vector2D from "./collisionWarning/Vector2D";
import * as VesselUtil from "./VesselUtil";
import { VESSEL_CATEGORY } from "@/config/const";
import Colors from "@/config/Colors";
import moment from "moment";

import * as turfHelpers from "@turf/helpers";
import { isNumber, isObject, isString } from "lodash";
import { firebase } from "@/firebase/firebaseConfig";

/**
 * Point feature 生成 (船舶表示)
 * @param vesselId
 * @param data
 * @param vesselInfo
 * @param currentEpochSeconds
 * @returns {{geometry: {coordinates: *[], type: string}, type: string, properties: {tco: string, c: *, d: *, h: *, i: string, co: *, n: string, o: number, r: *, s: *, t: *, v: *, id: *}}}
 */
export const createPointFeature = (
  vesselId,
  data,
  vesselInfo,
  currentEpochSeconds,
  isSelected,
  isOwnVessel,
  isSelectedVessel
) => {
  let diff = currentEpochSeconds - data.t / 1000;
  let category =
    vesselInfo && vesselInfo.c ? vesselInfo.c : VESSEL_CATEGORY.OTHER;

  return {
    type: "Feature",
    geometry: {
      type: "Point",
      coordinates: [
        data.l.longitude || data.l._long,
        data.l.latitude || data.l._lat,
      ],
    },
    properties: {
      id: vesselId,
      d: data.d,
      h: data.h,
      s: data.s,
      t: data.t,
      v: data.v,
      r: data.h || data.d,
      c: category,
      i: VesselUtil.getSymbolIconNameLightWeight(category, data.v, diff),
      o: VesselUtil.getSimpleOpacity(category, diff),
      tco: isSelected ? "#DA514B" : "#ffffff", // テキストカラー
      co: VESSEL_CATEGORY.properties[category].color,
      n: vesselInfo ? (vesselInfo.n_j ? vesselInfo.n_j : vesselInfo.n) : "",
      is_size: vesselInfo && vesselInfo.l && vesselInfo.w ? 1 : 0,
      iom: isOwnVessel ? "vessel-owned-marker-25" : "",
      ism: isSelectedVessel ? "vessel-selected-marker-25" : "",
    },
  };
};

// vector作成用に、vesselPosition likeなオブジェクトを作成
export const createVesselPositionLike = (
  vesselHistoryPoint // object NOTE: vesselHistoryData.points[index]
) => {
  if (!isObject(vesselHistoryPoint)) return null;
  const { epoch, d, lat, lon, heading: h, speed: v } = vesselHistoryPoint;
  // 引数の型を示せる・今後必要となる可能性があるので不要なバリデーションもコメントとして残す
  if (!isNumber(epoch)) return null;
  // if (!isNumber(d)) return null;
  if (!isNumber(lat)) return null;
  if (!isNumber(lon)) return null;
  // if (!isNumber(h)) return null;
  // if (!isNumber(v)) return null;
  return {
    t: epoch * 1000, // epochは秒単位なので、ミリ秒に変換する
    d: isNumber(d) ? d : h, // dがない場合は、履歴のheadingを使用する
    l: new firebase.firestore.GeoPoint(lat, lon),
    h: h || 0,
    v: v || 0,
  };
};

// vector作成用に、vesselInfo likeなオブジェクトを作成
export const createVesselInfoLike = (
  vesselHistoryData // object NOTE: vesselHistoryData
) => {
  if (!isObject(vesselHistoryData)) return null;
  const { c, size } = vesselHistoryData;
  const { l, d_sb, d_b, d_s, d_p, w } = size || {};
  // 引数の型を示せる・今後必要となる可能性があるので不要なバリデーションもコメントとして残す
  // if (!isNumber(l)) return null;
  // if (!isNumber(w)) return null;
  // if (!isNumber(d_sb)) return null;
  // if (!isNumber(d_b)) return null;
  // if (!isNumber(c)) return null;
  // if (!isNumber(d_s)) return null;
  // if (!isNumber(d_p)) return null;
  return {
    l,
    d_sb,
    d_b,
    c,
    d_s,
    d_p,
    w,
  };
};

// vesselHistoryDataとポイントのデータから、vectorFeatureを作成する
export const createVectorFeatureWrapper = (
  vesselId, // string (number of string)
  vesselHistoryData, // vesselHistoryData
  vesselHistoryPointData, // vesselHistoryData.points[index]
  vectorMinute = 6 // number
) => {
  if (!isString(vesselId)) return null;
  if (isNaN(Number(vesselId))) return null; // historyのidはstring、positionのidはnumber
  if (!isObject(vesselHistoryData)) return null;
  if (!isObject(vesselHistoryPointData)) return null;

  return createVectorFeature(
    vesselId,
    createVesselPositionLike(vesselHistoryPointData),
    createVesselInfoLike(vesselHistoryData),
    vectorMinute
  );
};
/**
 * Vector feature 生成 (方位速度ベクトル表示)
 * @param vesselId: string (number of string)
 * @param data: Object (vesselPosition) t, d, v, h
 * @param vesselInfo: Object (vesselInfo) c, w, l, d_sb, d_p, d_b, d_s
 * @param vectorMinute: number
 * @returns {{geometry: {coordinates: *[][], type: string}, type: string, properties: {c: *, id: *}}}
 */
export const createVectorFeature = (
  vesselId,
  data,
  vesselInfo,
  vectorMinute
) => {
  let currentEpochSeconds = moment.utc().unix();
  let diff = currentEpochSeconds - data.t / 1000;
  let category =
    vesselInfo && vesselInfo.c ? vesselInfo.c : VESSEL_CATEGORY.OTHER;

  const baseLocation = VesselUtil.getCenterCoordinationFromAIS(
    vesselInfo,
    data
  );
  const toPoint = LightWeightGlobalLogic.getArrivalPoint(
    baseLocation,
    data.d,
    (data.v * CollisionLogicUtil.MILE * (vectorMinute * 60.0)) / 3600.0
  );

  return {
    type: "Feature",
    geometry: {
      type: "LineString",
      coordinates: [
        [baseLocation.longitude, baseLocation.latitude],
        [toPoint.longitude, toPoint.latitude],
      ],
    },
    properties: {
      id: vesselId,
      c: category,
      o: VesselUtil.getVectorOpacity(diff),
      // エリア外のフィルタ用
      lon: baseLocation.longitude,
      lat: baseLocation.latitude,
    },
  };
};

/**
 * Line string feature 生成 (航跡)
 *
 * @param vesselId
 * @param vesselInfo
 * @param wakeHistories
 * @returns {Array}
 * @deprecated createWakeLineStringFeature
 */
export const createWakeLineFeatures = wakeHistories => {
  const coordinates = [];

  if (wakeHistories) {
    wakeHistories.forEach(position => {
      coordinates.push([position.lon, position.lat]);
    });
  }

  return {
    type: "Feature",
    geometry: {
      type: "LineString",
      coordinates: coordinates,
    },
    properties: {},
  };
};

/**
 *  航跡のLineStringのFeatureを生成
 *
 * @param wakeHistories 航跡
 * @param color カラーコード
 * @returns Feature <LineString> - LineString Feature
 */
export const createWakeLineStringFeature = (wakeHistories, properties) => {
  if (wakeHistories === null) return null;

  const coordinates = [];
  wakeHistories.forEach(position => {
    coordinates.push([position.lon, position.lat]);
  });

  if (coordinates.length < 2) {
    coordinates.push(coordinates[0]);
  }
  return turfHelpers.lineString(coordinates, properties);
};

/**
 *  Feature<Points>を生成
 *
 * @param coordinates Array<Array<number>>
 * @param properties Object key-value
 * @returns Feature<MultiPoint> - MultiPoint of Features
 */
export const createMarkerPointFeature = (coordinates, properties) => {
  return turfHelpers.point(coordinates, properties);
};

/**
 *  Feature<Points>を生成
 *
 * @param coordinates Array<Array<number>>
 * @param properties Object key-value
 * @returns Feature<MultiPoint> - MultiPoint of Features
 */
export const createsPointFeature = (coordinates, properties) => {
  return turfHelpers.point(coordinates, properties);
};

export const createOwnVesselPolygonFeature = (coordinates, properties) => {
  return turfHelpers.polygon(coordinates, properties);
};

/**
 *  FeatureCollectionを生成
 *
 * @param features GeoJson<Array>
 * @returns FeatureCollection - FeatureCollection of Features
 */
export const createFeatureCollection = features => {
  return turfHelpers.featureCollection(features);
};

/**
 * Vector feature 生成 (方位速度ベクトル表示)
 * @param vesselId
 * @param position
 * @param info
 * @returns {Array}
 */
export const createPolygonFeature = (vesselId, position, info) => {
  let vesselFeatures = [];

  if (info) {
    const vesselBody = createVesselBodyCoords(info, position);
    if (vesselBody) {
      const aisTriangle = createAISTriangleCoords(info, position);
      const starboardLight = createStarboardLightCoords(vesselBody);
      const portLight = createPortLightCoords(vesselBody);
      const color = Colors.getVesselCategoryColor(info.c);

      vesselFeatures.push({
        type: "Feature",
        geometry: {
          type: "Polygon",
          coordinates: [
            [
              [vesselBody.backRight.longitude, vesselBody.backRight.latitude],
              [vesselBody.frontRight.longitude, vesselBody.frontRight.latitude],
              [
                vesselBody.frontCenter.longitude,
                vesselBody.frontCenter.latitude,
              ],
              [vesselBody.frontLeft.longitude, vesselBody.frontLeft.latitude],
              [vesselBody.backLeft.longitude, vesselBody.backLeft.latitude],
              [vesselBody.backRight.longitude, vesselBody.backRight.latitude],
            ],
          ],
        },
        properties: {
          id: vesselId,
          c: info.c || 9,
          co: color,
        },
      });

      vesselFeatures.push({
        type: "Feature",
        geometry: {
          type: "Polygon",
          coordinates: [portLight || []],
        },
        properties: {
          id: vesselId,
          c: info.c || 9,
          co: "#FF0002",
        },
      });
      vesselFeatures.push({
        type: "Feature",
        geometry: {
          type: "Polygon",
          coordinates: [starboardLight || []],
        },
        properties: {
          id: vesselId,
          c: info.c || 9,
          co: "#54FF00",
        },
      });
      vesselFeatures.push({
        type: "Feature",
        geometry: {
          type: "Polygon",
          coordinates: [aisTriangle || []],
        },
        properties: {
          id: vesselId,
          c: info.c || 9,
          co: "#FFFFFF",
        },
      });
    }
  }

  return vesselFeatures;
};

/**
 * ポリゴン生成 (船体)
 *
 * @param info
 * @param position
 * @returns {null|{frontRight: Location, frontCenter: Location, frontLeft: Location, backLeft: Location, backRight: Location}}
 */
export const createVesselBodyCoords = (info, position) => {
  if (!info.l || !info.w) {
    return null;
  }

  const length = info.l;
  const width = info.w;
  const location = { latitude: position.l._lat, longitude: position.l._long };

  const left = info.d_p ? info.d_p : width / 2.0;
  const right = info.d_sb ? info.d_sb : width / 2.0;
  const front = info.d_b ? info.d_b : length / 2.0;
  const back = info.d_s ? info.d_s : length / 2.0;

  const backCenter = LightWeightGlobalLogic.getArrivalPoint(
    location,
    position.h + 180,
    back
  );
  const backRight = LightWeightGlobalLogic.getArrivalPoint(
    backCenter,
    position.h + 90,
    right
  );
  const backLeft = new Location(
    ((left + right) / right) * backCenter.latitude -
      (left / right) * backRight.latitude,
    ((left + right) / right) * backCenter.longitude -
      (left / right) * backRight.longitude
  );
  const frontRight = LightWeightGlobalLogic.getArrivalPoint(
    backRight,
    position.h,
    (front + back) * 0.85
  );
  const frontLeft = new Location(
    frontRight.latitude + backLeft.latitude - backRight.latitude,
    frontRight.longitude + backLeft.longitude - backRight.longitude
  );
  const trueBackCenter = new Location(
    (backRight.latitude + backLeft.latitude) / 2.0,
    (backRight.longitude + backLeft.longitude) / 2.0
  );
  const frontCenter = LightWeightGlobalLogic.getArrivalPoint(
    trueBackCenter,
    position.h,
    front + back
  );
  return {
    backRight: backRight,
    frontRight: frontRight,
    frontCenter: frontCenter,
    frontLeft: frontLeft,
    backLeft: backLeft,
  };
};

/**
 * ポリゴン生成 (AIS)
 * @param info
 * @param position
 * @returns {*[][]|null}
 */
export const createAISTriangleCoords = (info, position) => {
  if (!info.w) {
    return null;
  }

  const width = info.w;
  const innerLength = width / (2.0 * Math.sqrt(3.0));
  const location = { latitude: position.l._lat, longitude: position.l._long };

  const top = LightWeightGlobalLogic.getArrivalPoint(
    location,
    position.h,
    innerLength
  );
  const rightDir = position.h > 240.0 ? position.h - 240.0 : position.h + 120.0;
  const right = LightWeightGlobalLogic.getArrivalPoint(
    location,
    rightDir,
    innerLength
  );
  const leftDir = position.h < 120.0 ? position.h + 240.0 : position.h - 120.0;
  const left = LightWeightGlobalLogic.getArrivalPoint(
    location,
    leftDir,
    innerLength
  );

  return [
    [top.longitude, top.latitude],
    [right.longitude, right.latitude],
    [left.longitude, left.latitude],
  ];
};

/**
 * ポリゴン生成 (右舷灯)
 * @param info
 * @param position
 * @returns {*[][]|null}
 */
export const createStarboardLightCoords = vesselBody => {
  if (!vesselBody) {
    return null;
  }

  const backRight = vesselBody.backRight.toMercatorAtlasPosition();
  const frontRight = vesselBody.frontRight.toMercatorAtlasPosition();

  const posA = new Vector2D(
    0.1 * backRight.x + 0.9 * frontRight.x,
    0.1 * backRight.y + 0.9 * frontRight.y
  );
  const posB = new Vector2D(
    0.25 * backRight.x + 0.75 * frontRight.x,
    0.25 * backRight.y + 0.75 * frontRight.y
  );
  const posAplusB = new Vector2D(posA.x + posB.x, posA.y + posB.y);
  const posAminusB = new Vector2D(posA.x - posB.x, posA.y - posB.y);
  const posC = new Vector2D(
    0.5 * posAplusB.x +
      0.5 * Math.sqrt(3.0) * posAminusB.rotate(-Math.PI / 2.0, null).x,
    0.5 * posAplusB.y +
      0.5 * Math.sqrt(3.0) * posAminusB.rotate(-Math.PI / 2.0, null).y
  );

  const posACoord = posA.toLocation();
  const posBCoord = posB.toLocation();
  const posCCoord = posC.toLocation();
  return [
    [posACoord.longitude, posACoord.latitude],
    [posBCoord.longitude, posBCoord.latitude],
    [posCCoord.longitude, posCCoord.latitude],
    [posACoord.longitude, posACoord.latitude],
  ];
};

/**
 * ポリゴン生成 (左舷灯)
 * @param info
 * @param position
 * @returns {*[][]|null}
 */
export const createPortLightCoords = vesselBody => {
  if (!vesselBody) {
    return null;
  }

  const backLeft = vesselBody.backLeft.toMercatorAtlasPosition();
  const frontLeft = vesselBody.frontLeft.toMercatorAtlasPosition();

  const posA = new Vector2D(
    0.1 * backLeft.x + 0.9 * frontLeft.x,
    0.1 * backLeft.y + 0.9 * frontLeft.y
  );
  const posB = new Vector2D(
    0.25 * backLeft.x + 0.75 * frontLeft.x,
    0.25 * backLeft.y + 0.75 * frontLeft.y
  );
  const posAplusB = new Vector2D(posA.x + posB.x, posA.y + posB.y);
  const posAminusB = new Vector2D(posA.x - posB.x, posA.y - posB.y);
  const posC = new Vector2D(
    0.5 * posAplusB.x +
      0.5 * Math.sqrt(3.0) * posAminusB.rotate(Math.PI / 2.0, null).x,
    0.5 * posAplusB.y +
      0.5 * Math.sqrt(3.0) * posAminusB.rotate(Math.PI / 2.0, null).y
  );

  const posACoord = posA.toLocation();
  const posBCoord = posB.toLocation();
  const posCCoord = posC.toLocation();
  return [
    [posACoord.longitude, posACoord.latitude],
    [posBCoord.longitude, posBCoord.latitude],
    [posCCoord.longitude, posCCoord.latitude],
    [posACoord.longitude, posACoord.latitude],
  ];
};
